import { useEffect } from "react";
import { getAllSceneNodes } from "@/document/model/document";
import type { Paint } from "@/document/model/styles";
import { documentStore, selectionStore, uiStore } from "@/editor/store";
import { useStoreSubscribe } from "@/lib/store";
import type { ColorSamplerRequestWithCommit } from "../colorSampler";
import { ColorPickerPopover } from "./ColorPickerPopover";
import { topSelectionPaint, type SelectionPaintKind } from "./selectionPaintPicker";
import type { PickerMode } from "./types";

/**
 * Floating fill/stroke picker opened by G / Shift+G.
 * Mounted at the shell level so it stays available when UI chrome is hidden.
 */
export function SelectionPaintPickerOverlay({
  activeColorSamplerSourceId,
  onChange,
  onColorSampleRequest,
  onColorSampleModeChange,
  onColorSampleCancel,
}: {
  activeColorSamplerSourceId?: string;
  onChange: (nodeIds: string[], kind: SelectionPaintKind, paint: Paint) => void;
  onColorSampleRequest: (request: ColorSamplerRequestWithCommit) => void;
  onColorSampleModeChange: (sourceId: string, mode: PickerMode) => void;
  onColorSampleCancel: (sourceId?: string) => void;
}) {
  const document = useStoreSubscribe(documentStore.document);
  const selection = useStoreSubscribe(selectionStore.selection);
  const selectionPaintPicker = useStoreSubscribe(uiStore.selectionPaintPicker);
  const selectedNodeIds = selection.selectedNodeIds;
  const mode = selection.mode;

  useEffect(() => {
    if (!selectionPaintPicker) {
      return;
    }
    const selectedSet = new Set(selectedNodeIds);
    const sameSelection = selectedSet.size === selectionPaintPicker.selectionNodeIds.length
      && selectionPaintPicker.selectionNodeIds.every(nodeId => selectedSet.has(nodeId));
    if (!sameSelection || mode !== "select") {
      uiStore.closeSelectionPaintPicker();
    }
  }, [mode, selectedNodeIds, selectionPaintPicker]);

  if (!selectionPaintPicker) {
    return null;
  }

  const nodes = getAllSceneNodes(document);
  const selectionPaintNode = nodes.find(candidate => candidate.id === selectionPaintPicker.primaryNodeId)
    ?? nodes.find(candidate => selectionPaintPicker.nodeIds.includes(candidate.id));
  const selectionPickerPaint = selectionPaintNode
    ? topSelectionPaint(document, selectionPaintNode, selectionPaintPicker.kind)
    : undefined;
  if (!selectionPaintNode || !selectionPickerPaint) {
    return null;
  }

  return (
    <ColorPickerPopover
      title={selectionPaintPicker.kind === "fill" ? "Fill" : "Stroke"}
      paint={selectionPickerPaint}
      allowImage={
        selectionPaintPicker.kind === "fill"
        && selectionPaintPicker.nodeIds.length === 1
        && selectionPaintNode.type !== "text"
      }
      activeColorSamplerSourceId={activeColorSamplerSourceId}
      onColorSampleRequest={onColorSampleRequest}
      onColorSampleModeChange={onColorSampleModeChange}
      onClose={() => {
        uiStore.closeSelectionPaintPicker();
        onColorSampleCancel();
      }}
      onChange={paint => onChange(
        selectionPaintPicker.nodeIds,
        selectionPaintPicker.kind,
        paint,
      )}
    />
  );
}
